import { Injectable } from '@angular/core';
import { Validators } from '@angular/forms';
import { CrudViewColumnType, CrudViewFormItemType, createDateDisplayFormatFunction } from '@rappider/rappider-components/utils';
import { ListMode } from '@rappider/rappider-components';
import { EntityConfigMetadata, EntityField } from '../models/entity-config.models';
import { ProjectModelWithRelations } from '@rappider/rappider-sdk';

@Injectable({
  providedIn: 'root'
})
export class EntityMetabaseService {
  constructor() { }

  /**
   * Generates an EntityConfigMetadata object from a given project entity definition.
   * @param entity The raw entity object from your project.
   * @returns A fully formed EntityConfigMetadata object.
   */
  public generateConfig(entity: ProjectModelWithRelations): EntityConfigMetadata {
    if (!entity || !entity.name || !entity.fields) {
      throw new Error('Invalid entity object provided.');
    }

    const entityFields = this.generateEntityFields(entity.fields);
    const titleFieldName = this.findTitleField(entity.fields);

    return {
      // --- Basic Entity Information ---
      name: this.toPascalCase(entity.name),
      title: this.toTitleCase(entity.name),
      description: entity.description || `Manage ${this.toTitleCase(entity.name)} entities.`,
      apiPath: entity.basePath,
      dataFetchedFrom: entity?.dataFetchedFrom || 'loopback-api', // Default value
      titleFieldName: titleFieldName,

      // --- Data Transformation Functions (can be customized) ---
      entityDataCreateTransformFunction: (data: any) => {
        console.log('Transforming data for CREATE:', data);
        // Add any specific data transformations needed before sending to the API
        return data;
      },
      entityDataUpdateTransformFunction: (data: any) => {
        console.log('Transforming data for UPDATE:', data);
        // Add any specific data transformations needed before sending to the API
        return data;
      },

      // --- Field Definitions ---
      fields: entityFields,

      /* Ai search */
      showSearchWithAiButton: true,

      /* Default list mode to Grid */
      listMode: ListMode.Grid,

      /* Saved grid configuration */
      savedGridConfig: entity.gridConfig || undefined,
    };
  }

  /**
   * Transforms the raw fields from the entity definition into EntityField objects.
   * @param rawFields The array of fields from the entity.
   * @returns An array of EntityField objects.
   */
  private generateEntityFields(rawFields: any[]): EntityField[] {
    return rawFields
      .filter(field => !field.isMixinProperty) // Exclude mixin properties from the UI config
      .map(field => {
        const title = this.toTitleCase(field.name);
        const displayFieldType = this.mapDisplayFieldType(field);
        const jsonSchema = this.mapJsonSchema(field);

        const fieldConfig: EntityField = {
          fieldName: field.name,
          title: title,
          dataType: this.mapDataType(field.type),
          displayFieldType: displayFieldType,
          formFieldType: this.mapFormFieldType(field),
          isRequired: !!field.isRequired,
          hint: field.description || `Enter the ${title}.`,
          inputDescription: field.description || '',
          isMixinProperty: !!field.isMixinProperty,
          joyride: field.joyrideStep,
          text: field.text,
          default: field.default
        };

        if (jsonSchema) {
          fieldConfig.jsonSchema = jsonSchema;
        }

        if (displayFieldType === CrudViewColumnType.Date || displayFieldType === CrudViewColumnType.DateTime) {
          fieldConfig.displayFormatFunction = createDateDisplayFormatFunction(displayFieldType);
        }

        if (field.isRequired) {
          fieldConfig.formValidators = [
            {
              type: Validators.required,
              errorKey: 'required',
              errorMessage: `${title} is required.`,
            },
          ];
        }

        return fieldConfig;
      });
  }

  /**
   * Determines the most suitable field to be used as the title display field.
   * @param fields The array of fields from the entity.
   * @returns The name of the field to be used as the title.
   */
  private findTitleField(fields: any[]): string {
    const nonMixinFields = fields.filter(f => !f.isMixinProperty);
    const preferredField = nonMixinFields.find(f => f.name.toLowerCase() === 'name' || f.name.toLowerCase() === 'title');
    if (preferredField) {
      return preferredField.name;
    }
    const firstStringField = nonMixinFields.find(f => f.type.toLowerCase() === 'string');
    if (firstStringField) {
      return firstStringField.name;
    }
    return nonMixinFields.length > 0 ? nonMixinFields[0].name : 'id';
  }


  // --- Helper Functions ---

  /**
   * Converts a string to PascalCase.
   * Example: "my_entity_name" -> "MyEntityName"
   */
  private toPascalCase(str: string): string {
    return `${str}`
      .replace(new RegExp(/[-_]+/, 'g'), ' ')
      .replace(new RegExp(/[^\w\s]/, 'g'), '')
      .replace(
        new RegExp(/\s+(.)(\w*)/, 'g'),
        ($1, $2, $3) => `${$2.toUpperCase() + $3.toLowerCase()}`
      )
      .replace(new RegExp(/\w/), s => s.toUpperCase());
  }

  /**
   * Converts a string from camelCase or snake_case to a human-readable Title Case.
   * Example: "FinancialsQ22025Csv" -> "Financials Q2 2025 Csv"
   * Example: "Login_email" -> "Login Email"
   */
  private toTitleCase(str: string): string {
    if (!str) {
      return '';
    }
    return str
      .replace(/_/g, ' ')
      // Add space between number and letter, and before uppercase letters
      .replace(/([a-z])([A-Z0-9])/g, '$1 $2')
      .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
      .replace(/^./, (s) => s.toUpperCase())
      .trim();
  }

  /**
   * Maps the entity's field type to the metabase dataType string.
   */
  private mapDataType(type: string): string {
    // The target interface uses a generic string, so we just pass the type through.
    return type.toLowerCase();
  }

  /**
   * Maps the entity's field definition to a CrudViewColumnType.
   */
  private mapDisplayFieldType(field: any): CrudViewColumnType {
    if (field.isPrimaryKey) {
      return CrudViewColumnType.Link;
    }
    switch (field.type.toLowerCase()) {
      case 'number':
        return CrudViewColumnType.Number;
      case 'boolean':
        return CrudViewColumnType.Boolean;
      case 'date':
        if (field?.jsonSchema?.format) {
          return CrudViewColumnType.AutoDate;
        } else {
          return CrudViewColumnType.DateTime;
        }
      default:
        return CrudViewColumnType.Text;
    }
  }

  /**
 * Maps the entity's field definition to a CrudViewColumnType.
 */
  private mapJsonSchema(field: any) {
    if (field.isPrimaryKey) {
      return CrudViewColumnType.Link;
    }
    if (field.type.toLowerCase() === 'date' && field?.jsonSchema) {
      return field.jsonSchema;
    }
  }

  /**
   * Maps the entity's field type to a CrudViewFormItemType.
   * This is now smarter and checks field names for hints.
   */
  private mapFormFieldType(field: any): CrudViewFormItemType {
    const fieldName = field.name.toLowerCase();
    if (fieldName.includes('description') || fieldName.includes('notes') || fieldName.includes('comment')) {
      return CrudViewFormItemType.TextArea;
    }

    switch (field.type.toLowerCase()) {
      case 'number':
        return CrudViewFormItemType.Number;
      case 'boolean':
        return CrudViewFormItemType.CheckBox;
      case 'date':
        return CrudViewFormItemType.DateTimePicker;
      default:
        return CrudViewFormItemType.TextBox;
    }
  }
}

// // --- Example Usage ---

// // 1. Sample entity data as provided in the prompt
// const sampleProjectEntity = {
//   "createdDate": "2025-07-01T13:57:53.253Z",
//   "createdBy": "Tansel Ozkusaksiz",
//   "id": "ab947a36-52c7-46d4-ab28-0deb0440660f",
//   "name": "FinancialsQ22025Csv",
//   "basePath": "financials-q-22025-csvs",
//   "description": "Financial data for the second quarter of 2025.",
//   "fields": [
//       // Non-mixin properties
//       { "name": "Login_email", "type": "string", "isRequired": true, "isMixinProperty": false, "description": "The user's login email address." },
//       { "name": "Identifier", "type": "number", "isRequired": false, "isMixinProperty": false, "description": "A unique numeric identifier." },
//       { "name": "First_name", "type": "string", "isRequired": true, "isMixinProperty": false },
//       { "name": "detailed_comment", "type": "string", "isRequired": false, "isMixinProperty": false, "description": "Additional comments about the entry." },
//       // Mixin properties (will be filtered out)
//       { "name": "id", "type": "string", "isRequired": true, "isPrimaryKey": true, "isMixinProperty": true },
//       { "name": "createdDate", "type": "Date", "isRequired": false, "isMixinProperty": true },
//       { "name": "isDeleted", "type": "boolean", "isRequired": false, "isMixinProperty": true },
//   ]
// };

// // 2. Instantiate the service
// const generator = new MetabaseConfigGeneratorService();

// // 3. Generate the configuration
// const generatedConfig = generator.generateConfig(sampleProjectEntity);

// // 4. Log the result to the console to verify
// console.log('--- Generated Metabase Config ---');
// console.log(JSON.stringify(generatedConfig, null, 2));
